import React, { FunctionComponent, useState } from 'react';
import { ApiDataBinding, withApiData } from 'react-api-data';
import { compose } from 'recompose';
import { withTranslation, WithTranslation } from 'react-i18next';

// components
import Tooltip from 'components/utils/Tooltip';
import FormModal from './FormModal';
import { Alert } from 'reactstrap';
import EditBtnConditionalRenderer from 'components/utils/EditBtnConditionalRenderer';
import { Roles } from 'constants/literals';
import { ApiResponse, LinkFull, Router } from 'constants/api';

interface InputProps {
    router: Router;
    resourceStatus: string;
    exchange: string;
    linkId: string;
}

interface EnhanceProps {
    requestDisable: ApiDataBinding<ApiResponse<Router>>;
    requestEnable: ApiDataBinding<ApiResponse<Router>>;
    linkRequest: ApiDataBinding<LinkFull>;
}

type Props = EnhanceProps & InputProps & WithTranslation;

const enhance = compose<Props, InputProps>(
    withApiData({
        requestEnable: 'putEnablePeering',
        requestDisable: 'putDisablePeering',
        linkRequest: 'getMemberLink',
    }),
    withTranslation()
);

const AmsixPeeringModal: FunctionComponent<Props> = ({
    router,
    exchange,
    linkId,
    resourceStatus,
    requestDisable,
    requestEnable,
    linkRequest,
    t,
}) => {
    const [isOpen, setIsOpen] = useState(false);

    function toggle() {
        setIsOpen((isOpen) => !isOpen);
    }
    function onSubmit() {
        if (router.amsix_routeserver) {
            return requestDisable.perform({
                routerId: router.uuid,
                exchange,
                linkId,
            });
        } else {
            return requestEnable.perform({
                routerId: router.uuid,
                exchange,
                linkId,
            });
        }
    }

    function onSucces() {
        linkRequest.invalidateCache();
        linkRequest.perform({ linkId, exchange });
    }

    return (
        <React.Fragment>
            <FormModal
                header={t(`common.${router.amsix_routeserver ? 'disable' : 'enable'}`).concat(
                    ' ' + t('amsPeeringModal.peeringWithAms')
                )}
                isOpen={isOpen}
                toggle={toggle}
                initialValues={{}}
                onSubmit={onSubmit}
                onSuccess={onSucces}
                successMessage={
                    router.amsix_routeserver
                        ? t('notification.disableAmsPeeringSuccessful')
                        : t('notification.enableAmsPeeringSuccessful')
                }
            >
                {() => (
                    <React.Fragment>
                        {router.amsix_routeserver ? (
                            <>{t('amsPeeringModal.disablePeeringWarn')} </>
                        ) : (
                            <>
                                <Alert color="warning">
                                    <strong>{t('common.warning')}</strong>
                                    <p>{t('amsPeeringModal.warnParagraph1')}</p>
                                    <p>{t('amsPeeringModal.warnParagraph2')}</p>
                                    <p>{t('amsPeeringModal.warnParagraph3')}</p>
                                    <p>
                                        {t('amsPeeringModal.warnParagraph4')}
                                        <a
                                            href={t('amsPeeringModal.asSpecificationUrl')}
                                            rel="noopener noreferrer"
                                            target="_blank"
                                        >
                                            {t('common.here')}
                                        </a>
                                    </p>
                                    <p>{t('amsPeeringModal.warnParagraph5')}</p>
                                    <p>{t('amsPeeringModal.warnParagraph6')}</p>
                                </Alert>
                                {t('amsPeeringModal.enablePeeringWarn')}
                            </>
                        )}
                        {/* footer={<FormModalButtons loading={false} toggle={toggle} onSubmit={onSubmit} />} */}
                    </React.Fragment>
                )}
            </FormModal>
            <EditBtnConditionalRenderer
                data-test="edit-btn-renderer"
                resourceStatus={resourceStatus}
                roles={[Roles.admin, Roles.noc, Roles.qnoc, Roles.ixaas_client, Roles.customer]}
            >
                <Tooltip
                    data-test="peering-change-tooltip"
                    triggerBtn={
                        router.amsix_routeserver ? (
                            <i
                                className="fas fa-trash fa-pull-right mt-1 text-primary cursor-pointer"
                                title={t('amsPeeringModal.disablePeering')}
                                onClick={toggle}
                            />
                        ) : (
                            <i
                                className="fas fa-edit fa-pull-right mt-1 text-primary cursor-pointer"
                                title={t('amsPeeringModal.enablePeeering')}
                                onClick={toggle}
                            />
                        )
                    }
                >
                    {router.amsix_routeserver
                        ? t('amsPeeringModal.disablePeering')
                        : t('amsPeeringModal.enablePeeering')}
                </Tooltip>
            </EditBtnConditionalRenderer>
        </React.Fragment>
    );
};

export default enhance(AmsixPeeringModal);
